import React, { useRef } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

export interface BrandItem {
  id: string | number;
  name?: string;
  title?: string;
  offer?: string;
  logoUrl?: string;
  imageUrl?: string;
  link?: string;
  redirectUrl?: string;
  itemType?: string;
  ItemType?: string;
  itemId?: string | number | null;
  configJson?: any;
}

interface Props {
  items: BrandItem[];
  contentType?: "Custom" | "Dynamic" | string | number;
  sectionTitle?: string;
  onBrandClick?: (brand: BrandItem) => void;
}

const TopBrandsSection: React.FC<Props> = ({
  items = [],
  contentType,
  sectionTitle = "Top Brands",
  onBrandClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;

    const card = el.querySelector(".brand-card") as HTMLElement;
    if (!card) return;

    const gap = 40;
    const scrollAmount = card.offsetWidth + gap;

    el.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const handleItemClick = (item: BrandItem) => {
    // Check if Custom either at section level ("Custom") or item level ("Custom")
    const isCustom =
      String(contentType)?.toLowerCase() === "custom" ||
      item.itemType?.toLowerCase() === "custom" ||
      item.ItemType?.toLowerCase() === "custom";

    const targetUrl = item.redirectUrl || item.link;

    if (isCustom && targetUrl) {
      console.log("[TopBrandsSection] REDIRECTING CUSTOM URL TO:", targetUrl);
      const validUrl =
        targetUrl.startsWith("http://") || targetUrl.startsWith("https://")
          ? targetUrl
          : `https://${targetUrl}`;

      window.location.href = validUrl;
      return;
    }

    // Dynamic internal navigation via callback (Brand, Category, Product, etc.)
    onBrandClick?.(item);
  };

  return (
    <div className="w-full bg-white py-6">
      <div className="mx-auto text-center">
        <h2 className="flex items-center justify-center gap-2 text-2xl font-bold mb-5 text-logoHeading">
          {sectionTitle}
          <Sparkles className="w-6 h-6 text-yellow-500" />
        </h2>

        {/* SLIDER */}
        <div className="relative overflow-hidden">
          <div
            ref={scrollRef}
            className="flex gap-10 overflow-x-auto scroll-smooth px-6"
            style={{ scrollbarWidth: "none" }}
          >
            <style>{`div::-webkit-scrollbar { display: none; }`}</style>

            {items.map((item, index) => {
              const displayName = item.name || item.title || "Brand";
              const displayImage =
                item.logoUrl || item.imageUrl || "https://via.placeholder.com/150";

              return (
                <button
                  key={item.id ?? index}
                  onClick={() => handleItemClick(item)}
                  className="brand-card flex-shrink-0 w-[160px] text-center focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded-lg p-1 transition-transform active:scale-95 cursor-pointer"
                >
                  {/* IMAGE */}
                  <div className="relative w-full h-[120px] flex items-center justify-center overflow-hidden bg-white rounded-xl p-2">
                    <img
                      src={displayImage}
                      alt={displayName}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* TEXT */}
                  <p className="mt-3 font-semibold text-gray-800 text-sm truncate">
                    {displayName}
                  </p>

                  {item.offer && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {item.offer}
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {/* ARROWS */}
          <div className="flex justify-center gap-3 mt-6">
            <button
              onClick={() => scroll("left")}
              className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition"
              aria-label="Scroll Left"
            >
              <ChevronLeft size={18} />
            </button>

            <button
              onClick={() => scroll("right")}
              className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition"
              aria-label="Scroll Right"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBrandsSection;